import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { getOt, accionOt, deleteOt, downloadOtAdjunto, deleteOtAdjunto, getOtConfig, reabrirTareaSap, devolverTareaSap, confirmarTareaSap } from '../../api/client'
import { formatFecha, formatFechaHora } from '../unidades/tipos'
import { EstadoOt, OrigenOt, SubestadoOt, Semaforo } from './Etiquetas'
import { diasDesde, rechazoAbierto, tituloOt } from './otLogica'
import ConfirmarOf from './ConfirmarOf'
import Conversacion from './Conversacion'
import TrabajoOt from './TrabajoOt'
import { PlanificarMantenimiento } from './OtDeParte'
import Modal from '../Modal'

// Side panel of one OT: its data, attachments, log, and the steps the user
// may take now (the server says which: ot.acciones).
export default function OtPanel({ otId, onClose, onChanged }) {
  const { t } = useTranslation(['operations', 'common'])
  const navigate = useNavigate()
  const [ot, setOt] = useState(null)
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [funciones, setFunciones] = useState([])
  const [cancelando, setCancelando] = useState(false)     // the cancel confirmation dialog
  const [motivoCancelar, setMotivoCancelar] = useState('')

  useEffect(() => { getOtConfig().then(c => setFunciones(c.funciones)).catch(() => {}) }, [])

  useEffect(() => {
    let vivo = true
    getOt(otId)
      .then(o => { if (vivo) { setOt(o); setError('') } })
      .catch(() => { if (vivo) setError(t('ot.panel.noEncontrada')) })
    return () => { vivo = false }
  }, [otId, t])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const actuar = async (accion, extra = {}) => {
    // Planning needs its form: the admission tray has it.
    if (accion === 'aprobar_y_planificar') { navigate(`/operations/planta/admision?ot=${ot.id}`); return }
    // Cancelling asks for confirmation and a reason in its own dialog.
    if (accion === 'cancelar' && !extra.motivo) { setMotivoCancelar(''); setCancelando(true); return }
    if (accion === 'restaurar') {
      const destino = ot.acciones.find(a => a.accion === 'restaurar')?.estado_destino
      if (!window.confirm(t('ot.panel.restaurarConfirm', { codigo: ot.codigo, estado: t(`ot.estados.${destino}`, { defaultValue: destino }) }))) return
    }
    let datos = { ...extra }
    if (accion === 'devolver') {
      // Returning always says why (the requester sees it).
      const motivo = window.prompt(t('ot.panel.motivoPregunta'))
      if (!motivo?.trim()) return
      datos = { motivo: motivo.trim() }
    }
    setOcupado(true)
    setError('')
    try {
      const o = await accionOt(ot.id, accion, datos)
      setOt(o)
      onChanged?.(o)
    } catch (err) {
      const d = err.response?.data?.detail
      setError(d?.code === 'validacion'
        ? t('ot.faltanDatos', { lista: d.faltan.map(f => t(`ot.faltan.${f}`, { defaultValue: f })).join(', ') })
        : d?.message || t('ot.panel.fallo'))
    }
    setOcupado(false)
  }

  const borrar = async () => {
    if (!window.confirm(t('ot.panel.borrarConfirm', { codigo: ot.codigo }))) return
    try { await deleteOt(ot.id); onChanged?.({ ...ot, borrada: true }); onClose() } catch { setError(t('ot.panel.fallo')) }
  }

  const quitarAdjunto = async (a) => {
    if (!window.confirm(t('ot.panel.quitarAdjuntoConfirm', { nombre: a.filename }))) return
    try { await deleteOtAdjunto(ot.id, a.id); setOt(await getOt(ot.id)) } catch { setError(t('ot.panel.fallo')) }
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/20" onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <aside className="w-full max-w-xl h-full bg-white shadow-2xl flex flex-col">
        <div className="px-5 pt-4 pb-3 border-b border-gray-100 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm font-semibold text-gray-500">{ot?.codigo || '…'}</span>
                {ot && <EstadoOt estado={ot.estado} />}
                {ot && <SubestadoOt subestado={ot.subestado} />}
                {ot && <OrigenOt origen={ot.origen} />}
              </div>
              <h2 className="text-lg font-semibold text-gray-900 break-words mt-1">
                {ot ? (tituloOt(ot) || t('ot.sinArticulo')) : ''}
              </h2>
              {ot?.articulo_codigo && ot?.articulo_descripcion && (
                <p className="text-xs font-mono text-gray-500">{ot.articulo_codigo}</p>
              )}
              {ot?.semaforo && <div className="mt-1.5"><Semaforo semaforo={ot.semaforo} conTexto /></div>}
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
          </div>
          {ot && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {MARCAS.map(m => {
                const puedeMarcar = ot.acciones.some(a => a.accion === m.marcar)
                const puedeQuitar = ot.acciones.some(a => a.accion === m.quitar)
                if (!puedeMarcar && !puedeQuitar) return null
                const marcada = m.activa(ot)
                return (
                  <label key={m.marcar} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-xs text-gray-700 cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={marcada} disabled={ocupado || (marcada ? !puedeQuitar : !puedeMarcar)}
                      onChange={() => actuar(marcada ? m.quitar : m.marcar)} />
                    {t(`ot.marcas.${m.id}`)}
                  </label>
                )
              })}
              {ot.acciones.filter(a => !ACCIONES_DE_MARCA.has(a.accion) && !['cancelar', 'planificar_mantenimiento'].includes(a.accion)).map(a => (
                <button key={a.accion} type="button" disabled={ocupado} onClick={() => actuar(a.accion)}
                  className="bg-gray-900 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-800 disabled:opacity-40">
                  {t(`ot.acciones.${a.accion}`, { defaultValue: a.accion })}
                </button>
              ))}
              {ot.acciones.some(a => a.accion === 'cancelar') && (
                <button type="button" disabled={ocupado} onClick={() => actuar('cancelar')}
                  className="border border-rose-200 text-rose-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-rose-50 disabled:opacity-40 ml-auto">
                  {t('ot.acciones.cancelar')}
                </button>
              )}
              {ot.puede_editar && (
                <button type="button" onClick={() => navigate(`/operations/planta/ot/nueva?ot=${ot.id}`)}
                  className="border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-50">
                  {t('ot.panel.editar')}
                </button>
              )}
              {ot.puede_editar && ot.estado === 'borrador' && (
                <button type="button" onClick={borrar} className="text-xs font-medium text-gray-400 hover:text-red-600 px-1">
                  {t('ot.panel.borrar')}
                </button>
              )}
            </div>
          )}
          {error && <p className="text-red-500 text-xs">{error}</p>}
        </div>

        {ot && (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
            {ot.estado === 'cancelada' && (
              <div className="rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                <span className="font-medium">{t('ot.panel.cancelada')}</span>
                {ot.eventos.find(e => e.accion === 'cancelar')?.comentario && <> {ot.eventos.find(e => e.accion === 'cancelar').comentario}</>}
                {ot.acciones.some(a => a.accion === 'restaurar') && (
                  <span className="block text-xs text-gray-500 mt-0.5">{t('ot.panel.canceladaHint')}</span>
                )}
              </div>
            )}
            {rechazoAbierto(ot) && (
              <RechazoSap tarea={rechazoAbierto(ot)} codigo={ot.codigo} puede={funciones.includes('ejecutar_cola_sap')} ocupado={ocupado} t={t}
                onAccion={async (fn) => {
                  setOcupado(true); setError('')
                  try { const o = await fn(); setOt(o); onChanged?.(o) }
                  catch (err) { setError(err.response?.data?.detail?.message || t('ot.panel.fallo')) }
                  setOcupado(false)
                }} />
            )}
            {ot.estado === 'devuelta' && ot.motivo_devolucion && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                <span className="font-medium">{t('ot.panel.motivoDevolucion')}:</span> {ot.motivo_devolucion}
              </div>
            )}
            {ot.parte && (
              <section className="rounded-lg border border-teal-200 bg-teal-50/50 px-3 py-2.5 space-y-2">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-xs text-gray-500">{t('ot.parte.deParte')}</span>
                  <button type="button" onClick={() => navigate(`/operations/partes?parte=${ot.parte.id}`)}
                    className="font-semibold text-gray-900 hover:underline">{t('ot.parte.corto', { numero: ot.parte.numero })} · {ot.parte.unidad_codigo}</button>
                  {ot.parte.implica_fs && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-semibold text-[11px]">F/S</span>}
                  <span className="text-xs text-gray-500">{ot.parte.abierto ? t('ot.parte.abierto') : t('ot.parte.cerrado')}</span>
                </div>
                <p className="text-[11px] text-gray-500">{t('ot.parte.ayuda')}</p>
                {ot.acciones.some(a => a.accion === 'planificar_mantenimiento') && (
                  <PlanificarMantenimiento ot={ot} t={t} onGuardar={datos => actuar('planificar_mantenimiento', datos)} />
                )}
              </section>
            )}
            {!ot.parte && <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <Dato label={t('ot.campos.proyecto')}>{ot.proyecto ? `${ot.proyecto.name}${ot.proyecto.code ? ` (${ot.proyecto.code})` : ''}` : '—'}</Dato>
              <Dato label={t('ot.campos.tipo')}>{t(`ot.tipos.${ot.tipo}`, { defaultValue: ot.tipo })}</Dato>
              {ot.tipo === 'servicio' && <>
                <Dato label={t('ot.campos.categoria')}>{ot.categoria?.nombre || '—'}</Dato>
                <Dato label={t('ot.campos.ot_relacionada')}>
                  {ot.ot_relacionada ? `${ot.ot_relacionada.codigo}${ot.ot_relacionada.titulo ? ` · ${ot.ot_relacionada.titulo}` : ''}` : '—'}
                </Dato>
              </>}
              {ot.tipo !== 'servicio' && <>
              <Dato label={t('ot.campos.cantidad')}>{ot.cantidad ?? '—'}</Dato>
              <Dato label={t('ot.campos.fecha_requerida')}>{ot.fecha_requerida ? formatFecha(ot.fecha_requerida) : '—'}</Dato>
              <Dato label={t('ot.campos.tiene_receta_sap')}>
                {ot.tiene_receta_sap == null ? '—' : ot.tiene_receta_sap ? t('common:yes', { defaultValue: 'Sí' }) : t('common:no', { defaultValue: 'No' })}
              </Dato>
              </>}
              <Dato label={t('ot.campos.solicitante')}>{ot.solicitante?.display_name || '—'}</Dato>
              <Dato label={t('ot.campos.grupo')}>
                {ot.grupo ? ot.grupo.nombre : ot.grupo_propuesto ? t('ot.grupoPropuesto', { nombre: ot.grupo_propuesto.nombre }) : '—'}
              </Dato>
              {ot.prioridad && <Dato label={t('ot.campos.prioridad')}>{t(`ot.prioridades.${ot.prioridad}`)}</Dato>}
              {ot.fecha_compromiso && <Dato label={t('ot.campos.fecha_compromiso')}>{formatFecha(ot.fecha_compromiso)}</Dato>}
              {ot.responsable && <Dato label={t('ot.campos.responsable')}>{ot.responsable.nombre}</Dato>}
              {ot.horas_estimadas != null && <Dato label={t('ot.campos.horas_estimadas')}>{ot.horas_estimadas}</Dato>}
              <Dato label={t('ot.campos.creada')}>{formatFechaHora(ot.creado_en)}</Dato>
              <Dato label={t('ot.campos.enEstado')}>{t('ot.dias', { count: diasDesde(ot.estado_desde) })}</Dato>
              {ot.descripcion && <Dato label={t('ot.campos.descripcion')} ancho>{ot.descripcion}</Dato>}
            </dl>}
            {ot.parte && (
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                <Dato label={t('ot.campos.responsable')}>{ot.responsable?.nombre || '—'}</Dato>
                <Dato label={t('ot.campos.prioridad')}>{ot.prioridad ? t(`ot.prioridades.${ot.prioridad}`) : '—'}</Dato>
                <Dato label={t('ot.campos.fecha_compromiso')}>{ot.fecha_compromiso ? formatFecha(ot.fecha_compromiso) : '—'}</Dato>
                <Dato label={t('ot.campos.enEstado')}>{t('ot.dias', { count: diasDesde(ot.estado_desde) })}</Dato>
                <Dato label={t('ot.campos.creada')}>{formatFechaHora(ot.creado_en)}</Dato>
                <Dato label={t('ot.campos.solicitante')}>{ot.solicitante?.display_name || '—'}</Dato>
              </dl>
            )}

            {!ot.parte && ['en_ejecucion', 'terminada', 'cerrada'].includes(ot.estado) && (
              <PasosCierre ot={ot} funciones={funciones} ocupado={ocupado} t={t}
                onAccion={accion => actuar(accion)}
                onSap={async (tarea) => {
                  setOcupado(true); setError('')
                  try { const o = await confirmarTareaSap(tarea.id, {}); setOt(o); onChanged?.(o) }
                  catch (err) { setError(err.response?.data?.detail?.message || t('ot.panel.fallo')) }
                  setOcupado(false)
                }} />
            )}
            {ot.estado === 'planificada' && funciones.includes('ejecutar_cola_sap') && (
              <OfDesdePanel ot={ot} t={t} onHecho={o => { setOt(o); onChanged?.(o) }} />
            )}
            {ot.tareas_sap?.length > 0 && <TareasSap tareas={ot.tareas_sap} t={t} />}

            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('ot.panel.adjuntos')}</h3>
              {!ot.adjuntos.length ? <p className="text-sm text-gray-400">{t('ot.panel.sinAdjuntos')}</p> : (
                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
                  {ot.adjuntos.map(a => (
                    <li key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                      <span className="text-[11px] font-medium uppercase text-gray-500 w-14 shrink-0">{t(`ot.tiposAdjunto.${a.tipo}`, { defaultValue: a.tipo })}</span>
                      <button type="button" onClick={() => downloadOtAdjunto(ot.id, a).catch(() => setError(t('ot.panel.fallo')))}
                        className="flex-1 min-w-0 text-left truncate text-gray-800 hover:text-indigo-600 hover:underline">{a.filename}</button>
                      <span className="text-xs text-gray-400 shrink-0">{Math.max(1, Math.round(a.size_bytes / 1024))} KB</span>
                      {ot.puede_editar && (
                        <button type="button" onClick={() => quitarAdjunto(a)} className="text-gray-300 hover:text-red-500">×</button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {!ot.parte && <TrabajoOt ot={ot} t={t} puedeAsignar={funciones.includes('asignar_personas')} />}

            {/* A parte's conversation is the parte's (one place, not two). */}
            {ot.parte
              ? <p className="text-xs text-gray-500">{t('ot.parte.conversacion')}{' '}
                  <button type="button" onClick={() => navigate(`/operations/partes?parte=${ot.parte.id}`)} className="font-medium text-indigo-700 hover:underline">
                    {t('ot.parte.abrirParte')}</button></p>
              : <Conversacion otId={ot.id} t={t} onNuevo={() => onChanged?.()} />}

            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('ot.panel.bitacora')}</h3>
              <ol className="space-y-2">
                {ot.eventos.map(e => (
                  <li key={e.id} className="text-sm border-l-2 border-gray-200 pl-3">
                    <div className="flex flex-wrap items-center gap-x-2">
                      <span className="font-medium text-gray-900">{t(`ot.eventos.${e.accion}`, { defaultValue: e.accion })}</span>
                      {e.estado_nuevo && e.estado_nuevo !== e.estado_anterior && <EstadoOt estado={e.estado_nuevo} />}
                    </div>
                    <p className="text-xs text-gray-500">
                      {e.usuario_nombre || '—'}
                      {e.funcion && <> · {t('ot.panel.comoFuncion', { funcion: t(`ot.funciones.${e.funcion}`, { defaultValue: e.funcion }) })}</>}
                      {' · '}{formatFechaHora(e.fecha)}
                    </p>
                    {e.comentario && <p className="text-xs text-gray-600 mt-0.5">{e.comentario}</p>}
                  </li>
                ))}
              </ol>
            </section>
          </div>
        )}
      </aside>
      {cancelando && ot && (
        <Modal title={t('ot.panel.cancelarTitulo', { codigo: ot.codigo })} onClose={() => setCancelando(false)}>
          <div className="space-y-3 text-sm">
            <p className="text-gray-700">{t('ot.panel.cancelarTexto')}</p>
            {ot.folio_of && <p className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">{t('ot.panel.cancelarConOf', { folio: ot.folio_of })}</p>}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('ot.panel.cancelarMotivo')} *</label>
              <textarea rows={2} autoFocus className="input" value={motivoCancelar} onChange={e => setMotivoCancelar(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setCancelando(false)}
                className="bg-white border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">
                {t('ot.panel.noCancelar')}
              </button>
              <button type="button" disabled={ocupado || !motivoCancelar.trim()}
                onClick={() => { setCancelando(false); actuar('cancelar', { motivo: motivoCancelar.trim() }) }}
                className="bg-rose-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-rose-700 disabled:opacity-40">
                {t('ot.panel.siCancelar')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

// From the board too: a planned OT's OF PDF is attached here with the same
// validation as in the SAP queue (ConfirmarOf). Only when its OF task is
// pending; a task waiting for the BOM says so.
function OfDesdePanel({ ot, t, onHecho }) {
  const [abierto, setAbierto] = useState(false)
  const tareas = [...(ot.tareas_sap || [])].filter(x => x.transaccion === 'crear_liberar_of' && x.estado !== 'anulada')
  const of = tareas.sort((a, b) => b.id - a.id)[0]
  if (!of || !['pendiente', 'bloqueada'].includes(of.estado)) return null
  return (
    <section className="rounded-lg border border-indigo-200 bg-indigo-50/50 px-3 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-indigo-900">{t('ot.panel.ofTitulo')}</p>
          <p className="text-xs text-indigo-800/80">
            {of.estado === 'bloqueada' ? t('ot.panel.ofEsperaBom') : t('ot.panel.ofHint')}
          </p>
        </div>
        {of.estado === 'pendiente' && !abierto && (
          <button type="button" onClick={() => setAbierto(true)}
            className="text-xs font-medium bg-gray-900 text-white rounded-md px-3 py-1.5 hover:bg-gray-800">
            {t('ot.panel.ofAdjuntar')}
          </button>
        )}
      </div>
      {abierto && <ConfirmarOf tareaId={of.id} onHecho={o => { setAbierto(false); onHecho(o) }} onCancelar={() => setAbierto(false)} />}
    </section>
  )
}

// A planned OT held back by a rejected SAP task: why, and the two ways on
// (retry it in SAP, or send the OT back to its requester).
function RechazoSap({ tarea, codigo, puede, ocupado, t, onAccion }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900 space-y-2">
      <p>
        <span className="font-medium">{t('ot.panel.tareaRechazada', { tarea: t(`ot.transacciones.${tarea.transaccion}`) })}</span>
        {tarea.motivo_rechazo && <> {tarea.motivo_rechazo}</>}
      </p>
      {puede ? (
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={ocupado} onClick={() => onAccion(() => reabrirTareaSap(tarea.id))}
            className="text-xs font-medium border border-rose-300 bg-white rounded-md px-2.5 py-1 hover:bg-rose-100">{t('ot.cola.reabrir')}</button>
          <button type="button" disabled={ocupado}
            onClick={() => { if (window.confirm(t('ot.cola.devolverConfirm', { ot: codigo }))) onAccion(() => devolverTareaSap(tarea.id)) }}
            className="text-xs font-medium bg-rose-600 text-white rounded-md px-2.5 py-1 hover:bg-rose-700">{t('ot.cola.devolverBoton')}</button>
        </div>
      ) : <p className="text-xs">{t('ot.panel.rechazoSinFuncion')}</p>}
    </div>
  )
}

// From the kit's delivery to the closing, in order: what the group marks
// (received, finished), what the warehouse confirms from SAP (issue,
// receipt, OF closed) and the closing itself. Each step says who does it;
// only who holds that function can tick it.
function PasosCierre({ ot, funciones, ocupado, t, onAccion, onSap }) {
  const vig = {}
  for (const x of [...(ot.tareas_sap || [])].sort((a, b) => a.id - b.id)) if (x.estado !== 'anulada') vig[x.transaccion] = x
  const acc = new Set(ot.acciones.map(a => a.accion))
  const bodega = funciones.includes('ejecutar_cola_sap')
  const hecho = (x) => x?.estado === 'confirmada'
  const terminada = ['terminada', 'cerrada'].includes(ot.estado)
  const pasos = [
    { id: 'kit', hecho: true, quien: 'bodega' },
    { id: 'emision', hecho: hecho(vig.emision), quien: 'sap', tarea: vig.emision },
    { id: 'recibido', hecho: !!ot.marcas?.recibido_conforme, quien: 'grupo',
      marcar: acc.has('recibir_conforme') ? 'recibir_conforme' : null, quitar: acc.has('anular_recepcion') ? 'anular_recepcion' : null },
    // Finishing implies the material was received: unmarked, finishing marks it.
    { id: 'terminada', hecho: terminada, quien: 'grupo', boton: acc.has('terminar') ? 'terminar' : null,
      aviso: !ot.marcas?.recibido_conforme ? 'marcaRecibido' : null },
    { id: 'recibo', hecho: hecho(vig.recibo), quien: 'sap', tarea: vig.recibo },
    // Closing needs the issue and the receipt done in SAP (checked by the server too).
    { id: 'cerrar', hecho: ot.subestado === 'cierre_en_sap' || ot.estado === 'cerrada', quien: 'cierre', boton: acc.has('cerrar') ? 'cerrar' : null,
      bloqueo: !(hecho(vig.emision) && hecho(vig.recibo)) ? 'falta_sap' : null },
    { id: 'cerrar_of', hecho: ot.estado === 'cerrada', quien: 'sap', tarea: vig.cerrar_of },
  ]
  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('ot.pasos.titulo')}</h3>
      <ol className="border border-gray-200 rounded-lg divide-y divide-gray-100">
        {pasos.map((p, i) => {
          const sapPendiente = p.quien === 'sap' && p.tarea?.estado === 'pendiente'
          const puedeSap = sapPendiente && bodega
          const clickable = puedeSap || (p.quien === 'grupo' && (p.hecho ? p.quitar : p.marcar))
          return (
            <li key={p.id} className={`flex items-center gap-3 px-3 py-2 text-sm ${p.hecho ? 'bg-emerald-50/50' : ''}`}>
              <input type="checkbox" checked={p.hecho} disabled={ocupado || !clickable || (p.hecho && p.quien === 'sap')}
                onChange={() => {
                  if (puedeSap) { if (window.confirm(t('ot.pasos.confirmarSap', { paso: t(`ot.pasos.${p.id}`) }))) onSap(p.tarea) }
                  else if (p.quien === 'grupo') onAccion(p.hecho ? p.quitar : p.marcar)
                }}
                className={clickable ? 'cursor-pointer' : ''} />
              <span className="flex-1 min-w-0">
                <span className={`${p.hecho ? 'text-gray-900' : 'text-gray-700'}`}>{i + 1}. {t(`ot.pasos.${p.id}`)}</span>
                <span className="block text-[11px] text-gray-400">
                  {t(`ot.pasos.quien.${p.quien}`)}
                  {p.quien === 'sap' && !p.tarea && !p.hecho && <> · {t('ot.pasos.aunNo')}</>}
                  {p.quien === 'sap' && p.tarea?.estado === 'rechazada' && <> · <span className="text-rose-600">{t('ot.estadosTarea.rechazada')}</span></>}
                  {sapPendiente && !bodega && <> · {t('ot.pasos.enCola')}</>}
                </span>
              </span>
              {p.boton && !p.hecho && (
                <span className="flex flex-col items-end shrink-0">
                  <button type="button" disabled={ocupado || !!p.bloqueo}
                    onClick={() => { if (!p.aviso || window.confirm(t(`ot.pasos.confirmar.${p.aviso}`))) onAccion(p.boton) }}
                    title={p.bloqueo ? t(`ot.pasos.bloqueo.${p.bloqueo}`) : undefined}
                    className="text-xs font-medium bg-gray-900 text-white rounded-md px-2.5 py-1 hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed">
                    {t(`ot.acciones.${p.boton}`)}
                  </button>
                  {p.bloqueo && <span className="text-[10px] text-amber-700 mt-0.5 text-right max-w-[180px]">{t(`ot.pasos.bloqueo.${p.bloqueo}`)}</span>}
                  {p.aviso && <span className="text-[10px] text-gray-500 mt-0.5 text-right max-w-[180px]">{t(`ot.pasos.aviso.${p.aviso}`)}</span>}
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </section>
  )
}

// Marks inside a stage, shown as checkboxes: [marcar, quitar] actions of the flow.
const MARCAS = [
  { id: 'espera_componentes', marcar: 'marcar_espera_componentes', quitar: 'quitar_espera_componentes',
    activa: ot => ot.subestado === 'espera_componentes' },
]
// Steps the closing checklist (PasosCierre) takes care of: no separate buttons.
const ACCIONES_DE_MARCA = new Set([...MARCAS.flatMap(m => [m.marcar, m.quitar]),
  'recibir_conforme', 'anular_recepcion', 'terminar', 'cerrar'])

const ESTADO_TAREA = {
  pendiente: 'bg-amber-100 text-amber-800',
  bloqueada: 'bg-gray-100 text-gray-600',
  confirmada: 'bg-emerald-100 text-emerald-800',
  rechazada: 'bg-rose-100 text-rose-700',
  anulada: 'bg-gray-100 text-gray-500 line-through',
}

// What this OT has asked of SAP (the SAP queue), in order.
export function TareasSap({ tareas, t }) {
  const nombre = (id) => {
    const x = tareas.find(y => y.id === id)
    return x ? t(`ot.transacciones.${x.transaccion}`, { defaultValue: x.transaccion }) : `#${id}`
  }
  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('ot.panel.tareasSap')}</h3>
      <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
        {tareas.map(x => (
          <li key={x.id} className="flex items-center gap-3 px-3 py-2 text-sm">
            <span className="flex-1 min-w-0">
              <span className="text-gray-900">{t(`ot.transacciones.${x.transaccion}`, { defaultValue: x.transaccion })}</span>
              {x.estado === 'bloqueada' && x.depende_de_id && (
                <span className="block text-[11px] text-gray-500">{t('ot.panel.esperaA', { tarea: nombre(x.depende_de_id) })}</span>
              )}
              {x.folio_resultado && <span className="block text-[11px] text-gray-500">{t('ot.panel.folio', { folio: x.folio_resultado })}</span>}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${ESTADO_TAREA[x.estado] || 'bg-gray-100 text-gray-600'}`}>
              {t(`ot.estadosTarea.${x.estado}`, { defaultValue: x.estado })}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

function Dato({ label, ancho, children }) {
  return (
    <div className={ancho ? 'col-span-2' : ''}>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-gray-900 mt-0.5 whitespace-pre-wrap break-words">{children}</dd>
    </div>
  )
}
